<script>
	import constants from '$lib/constants';
	import { configureClient, isAuthenticated as _isAuthenticated, getAccessToken, login } from '@ctm/auth';
	import { appState, signedInUser } from '@ctm/auth/store';
	import { onMount } from 'svelte';
	import { isAuthenticated, accessToken } from '$lib/stores/user';
	import { loadSettings, settingsError, settingsLoaded, debtorSettings, applicationTiles, permissions, userDebtors } from '$lib/stores/settings';
	import { goto } from '$app/navigation';
	import { page } from '$app/stores';
	import { appInsights } from '$lib/helpers/appInsights';
	import { appUserLink } from '$lib/helpers/appUserLink.js';

	const auth0ClientOptions = {
		domain: constants.AUTH_DOMAIN,
		client_id: constants.AUTH_CLIENT_ID,
		redirect_uri: constants.AUTH_CALLBACK_URL,
		audience: 'http://concierge',
		scope: constants.AUTH_SCOPES,
	};

	const authConfig = {
		callbackLogoutUrl: constants.AUTH_CALLBACK_URL,
		auth0ClientOptions,
	};

	const { debtor } = $page.data;
	if (debtor) auth0ClientOptions.debtor = debtor;

	onMount(handleMount);

	async function handleMount() {
		if ($isAuthenticated) return;

		// handle an error that is thrown in the auth0 SDK when there is an active session
		// but reloading the page returns an error from auth0. This is the same error
		// when signing in but the SDK will throw the error when there is existing previous session
		try {
			if (appInsights) {
				// Pass operationId from AppInsights in auth requests so that
				// we can correlate app data with rules data in AppInsights
				auth0ClientOptions.operationId = encodeURIComponent(appInsights.context.telemetryTrace.parentID ?? appInsights.context.telemetryTrace.traceID);
			}
			await configureClient(authConfig);
		} catch (exception) {
			console.error(exception);
			const url = new URL('/', $page.url.origin);
			url.searchParams.set('error', exception.error || exception.message);

			if (exception.error_description) url.searchParams.set('error_description', exception.error_description);

			goto(url);
		}

		$isAuthenticated = await _isAuthenticated();

		if ($signedInUser) {
			$accessToken = await getAccessToken();
		}

		if ($isAuthenticated && $signedInUser && !$signedInUser.hasPasswordExpired) {
			await handleSettings();
			if ($page.route.id === '/(sigin-in)') {
				let userLink = $appState?.targetUrl ?? appUserLink('/dashboard');
				goto(userLink);
			}
		} else if ($page.route.id !== '/(sigin-in)') {
			// If the site has loaded in a new tab/window then we are most likely already authenticated in auth0
			// and getting to this code plus meeting the condition below means that the user may have 3rd party cookies
			// disabled and therefore we silently login to resume the session and load the target URL
			if (window.opener?.location.origin === $page.url.origin) {
				login({ targetUrl: $page.url.pathname + $page.url.search, login_prompt: 'none' });
			} else {
				const url = new URL(appUserLink('/'), $page.url.origin);
				url.searchParams.set('return_to', appUserLink($page.url.pathname));
				goto(url);
			}
		}
	}

	async function handleSettings() {
		try {
			const { data } = await loadSettings();
			$settingsError = false;
			$settingsLoaded = true;
			$debtorSettings = data.debtorSettings || {};
			$applicationTiles = data.applicationTiles;
			$permissions = data.permissions;
			$userDebtors = data.userDebtors || [];
		} catch (err) {
			$settingsError = true;
		}
	}
</script>
